import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI, referralAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, Mail, Lock, ArrowRight, Loader2, Gift,
  Shield, Zap, Star, Eye, EyeOff, Target, Brain
} from 'lucide-react';

export default function RegisterPage({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [refCode, setRefCode] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setRefCode(ref);
  }, [searchParams]);

  const planIntent = searchParams.get('plan');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.register({ email, password, name });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
      if (refCode) {
        try { await referralAPI.apply(refCode); } catch {}
      }
      if (planIntent && planIntent !== 'free') {
        navigate(`/subscription?upgrade=${planIntent}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel - Dark Brand */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'linear-gradient(135deg, #0D1E3A 0%, #0A1628 50%, #0F2847 100%)' }}>
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(13,148,136,0.4) 0%, transparent 70%)' }} />

        <div className="relative">
          <Link to="/" className="flex items-center gap-2.5 mb-16" data-testid="register-logo-link">
            <img src="/logos/Kairera-white.png" alt="Kairera" className="w-auto" style={{ height: '52px' }} />
          </Link>

          <h2 className="text-3xl font-extrabold tracking-tight leading-tight mb-4" style={{ color: '#F7FAFC' }}>
            Discover your<br />
            <span style={{ color: '#14B8A6' }}>career DNA</span>.
          </h2>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: '#7595CC' }}>
            Kairera's assessment engine reveals patterns in your career decisions, strengths,
            and potential. Things no resume can capture.
          </p>
        </div>

        <div className="relative space-y-5">
          <div className="space-y-3">
            {[
              { icon: Brain, label: 'Structured career intelligence assessments' },
              { icon: Target, label: 'Personalized career persona discovery' },
              { icon: Shield, label: 'Enterprise-grade security & privacy' },
              { icon: Zap, label: 'Instant ATS resume scoring' },
            ].map((item, i) => (
              <motion.div key={item.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(13,148,136,0.1)' }}>
                  <item.icon className="w-4 h-4" style={{ color: '#14B8A6' }} />
                </div>
                <span className="text-[13px] font-medium" style={{ color: '#9DB5DC' }}>{item.label}</span>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-3">
            <div className="flex -space-x-2">
              {['#0D9488', '#3B82F6', '#805AD5', '#F5841F'].map((c, i) => (
                <div key={i} className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-bold border-2"
                  style={{ background: `${c}30`, color: c, borderColor: '#0A1628' }}>
                  {['SC', 'MR', 'PS', 'LK'][i]}
                </div>
              ))}
            </div>
            <div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-2.5 h-2.5 fill-current" style={{ color: '#F5841F' }} />)}
              </div>
              <p className="text-[10px] font-medium" style={{ color: '#4A6593' }}>10,000+ professionals trust Kairera</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - White Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10" data-testid="register-mobile-logo">
            <img src="/logos/Kairera.png" alt="Kairera" className="w-auto" style={{ height: '44px' }} />
          </Link>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl font-extrabold tracking-tight mb-1" style={{ color: '#0D1E3A' }}>Create your account</h1>
            <p className="text-sm mb-6" style={{ color: '#64748b' }}>
              Free forever. No credit card required.
            </p>

            {refCode && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl mb-6"
                style={{ background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.15)' }}
                data-testid="referral-banner">
                <Gift className="w-4 h-4 flex-shrink-0" style={{ color: '#0D9488' }} />
                <span className="text-xs font-medium" style={{ color: '#0D9488' }}>
                  You've been referred! Sign up to help your friend earn Pro access.
                </span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              {error && (
                <div className="px-3 py-2 rounded-lg text-xs font-medium"
                  style={{ background: 'rgba(239,68,68,0.06)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.15)' }}>
                  {error}
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: '#334155' }}>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input type="text" value={name} onChange={e => setName(e.target.value)} required
                    placeholder="Your full name"
                    className="pl-10 h-11 text-sm rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900"
                    data-testid="register-name-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: '#334155' }}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    placeholder="you@company.com"
                    className="pl-10 h-11 text-sm rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900"
                    data-testid="register-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: '#334155' }}>Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    placeholder="Create a strong password"
                    className="pl-10 pr-10 h-11 text-sm rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900"
                    data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] mt-1" style={{ color: '#94a3b8' }}>Min 8 characters with a number</p>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 font-bold text-sm rounded-lg"
                style={{ background: '#0D9488', color: '#fff', boxShadow: '0 2px 12px rgba(13,148,136,0.2)' }}
                data-testid="register-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>

            <p className="text-center text-xs mt-5" style={{ color: '#94a3b8' }}>
              Already have an account?{' '}
              <Link to="/login" className="font-semibold" style={{ color: '#0D9488' }}
                data-testid="register-login-link">Sign in</Link>
            </p>

            <div className="flex items-center gap-3 justify-center mt-6 pt-4"
              style={{ borderTop: '1px solid #f1f5f9' }}>
              {[
                { icon: Shield, label: 'SOC 2 Compliant' },
                { icon: Lock, label: 'AES-256 Encryption' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-1.5">
                  <b.icon className="w-3 h-3" style={{ color: '#cbd5e1' }} />
                  <span className="text-[10px] font-medium" style={{ color: '#94a3b8' }}>{b.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
